import type { Action, BotUser, GameEvent, GlobalDocs, Session } from '@fakerpg/core';

/**
 * The web server's API (T3.7): the account comes from `GET /api/account`,
 * the session cookie is httpOnly and handled by the browser. No game data is
 * kept in the browser (ADR-016).
 */
export interface AccountView {
  /** `users/{id}`, dates as epoch ms (ADR-019). */
  user: BotUser;
  /** In-progress interactions kept by the server (ADR-021). */
  session: Session;
  /** Shared documents the screens display (the bet odds). */
  globals: Partial<GlobalDocs>;
  /** The account was created by this request (first visit, ADR-016). */
  created: boolean;
  /** The server accepts game actions (`WEB_WRITES`); false: view only. */
  writes: boolean;
}

/** `POST /api/action` answers (T3.8). */
export type ActionResult =
  | { kind: 'ok'; view: Omit<AccountView, 'created' | 'writes'>; events: GameEvent[] }
  /** A game rule refused it (core error code); nothing changed. */
  | { kind: 'refused'; code: string; params: Record<string, number | string> }
  | { kind: 'signedOut' }
  /** The server is read-only (`WEB_WRITES` unset). */
  | { kind: 'readOnly' }
  | { kind: 'tooFast' }
  | { kind: 'offline' }
  | { kind: 'error'; status: number };

export type AccountResult =
  | { kind: 'ok'; view: AccountView }
  /** No session, or it expired or was revoked (401). */
  | { kind: 'signedOut' }
  /** No game account and the server may not create one (403 `writesDisabled`). */
  | { kind: 'noAccount' }
  /** The network is unreachable. */
  | { kind: 'offline' }
  | { kind: 'error'; status: number };

export interface ServerApi {
  account(): Promise<AccountResult>;
  /** One game action; `key` makes a repeated delivery a no-op on the server. */
  action(action: Action, key: string): Promise<ActionResult>;
  /** Ends the session; false when the server could not be reached. */
  logout(): Promise<boolean>;
}

/** Where the "Sign in with Discord" button goes (a full navigation, not a fetch). */
export const LOGIN_URL = '/api/auth/login';

export function httpApi(fetchImpl: typeof fetch = (...args) => globalThis.fetch(...args)): ServerApi {
  return {
    async account() {
      let res: Response;
      try {
        res = await fetchImpl('/api/account', { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
      } catch {
        return { kind: 'offline' };
      }
      if (res.status === 401) return { kind: 'signedOut' };
      if (res.status === 403) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        return body?.error === 'writesDisabled' ? { kind: 'noAccount' } : { kind: 'signedOut' };
      }
      if (!res.ok) return { kind: 'error', status: res.status };
      try {
        return { kind: 'ok', view: (await res.json()) as AccountView };
      } catch {
        return { kind: 'error', status: res.status };
      }
    },
    async action(action, key) {
      let res: Response;
      try {
        res = await fetchImpl('/api/action', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ action, idempotencyKey: key }),
        });
      } catch {
        return { kind: 'offline' };
      }
      const body = (await res.json().catch(() => null)) as { error?: string; params?: Record<string, number | string> } | null;
      if (res.ok && body) return { kind: 'ok', view: body as unknown as Omit<AccountView, 'created' | 'writes'>, events: (body as unknown as { events: GameEvent[] }).events };
      if (res.status === 401) return { kind: 'signedOut' };
      if (res.status === 403 && body?.error === 'writesDisabled') return { kind: 'readOnly' };
      if (res.status === 422 && body?.error) return { kind: 'refused', code: body.error, params: body.params ?? {} };
      if (res.status === 429) return { kind: 'tooFast' };
      return { kind: 'error', status: res.status };
    },
    async logout() {
      try {
        // A POST: the browser adds the Origin header the server checks (ADR-023).
        const res = await fetchImpl('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
        return res.ok;
      } catch {
        return false;
      }
    },
  };
}

/** Why the login screen is shown: the server's `?login=` reasons, or a session that ended. */
export type LoginNotice = 'banned' | 'failed' | 'cancelled' | 'expired' | 'session';

const SERVER_NOTICES: readonly LoginNotice[] = ['banned', 'failed', 'cancelled', 'expired'];

/** Reads `#/?login=<reason>` (the server's login callback redirect). */
export function loginNoticeFrom(hash: string): LoginNotice | null {
  const query = hash.split('?')[1];
  if (!query) return null;
  const reason = new URLSearchParams(query).get('login');
  return SERVER_NOTICES.find((n) => n === reason) ?? null;
}
